"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StakeableLockedInput = exports.Secp256k1Input = exports.TransferableInput = exports.UserInput = void 0;
const utils_1 = require("../utils");
const constants_1 = require("./constants");
const signature_1 = require("./signature");
const types_1 = require("./types");
class UserInput {
    assetId;
    sourceChain;
    amount;
    addresses;
    threshold;
    destinationChain;
    locktime;
    stakeable;
    constructor(assetId, sourceChain, amount, addresses, threshold, destinationChain, locktime = BigInt(0), stakeable = false) {
        this.assetId = assetId;
        this.sourceChain = sourceChain;
        if (amount <= BigInt(0)) {
            throw new utils_1.InputError('user input amount must be greater than 0');
        }
        this.amount = amount;
        this.addresses = addresses;
        this.threshold = threshold;
        this.destinationChain = destinationChain;
        this.locktime = locktime;
        this.stakeable = stakeable;
    }
}
exports.UserInput = UserInput;
class TransferableInput extends signature_1.AbstractSignable {
    transactionId;
    utxoIndex;
    assetId;
    input;
    constructor(transactionId, utxoIndex, assetId, input) {
        super();
        this.transactionId = transactionId;
        this.utxoIndex = utxoIndex;
        this.assetId = assetId;
        this.input = input;
    }
    getTypeId() {
        return this.input.typeId;
    }
    getAmount() {
        return this.input.amount;
    }
    getAssetId() {
        return this.assetId;
    }
    getAddresses() {
        if (this.input.utxo === undefined) {
            throw new utils_1.SignatureError('cannot get addresses of read only inputs');
        }
        const addresses = [];
        const indices = this.input.addressIndices;
        for (let i = 0; i < indices.length; i++) {
            addresses.push(this.input.utxo.output.addresses[indices[i]]);
        }
        return addresses;
    }
    getThreshold() {
        if (this.input.utxo === undefined) {
            throw new utils_1.SignatureError('cannot get threshold of read only inputs');
        }
        return this.input.utxo.output.threshold;
    }
    serialize() {
        const inputBuffer = this.input.serialize();
        const buffer = utils_1.JuneoBuffer.alloc(constants_1.TransactionIdSize + 4 + constants_1.AssetIdSize + inputBuffer.length);
        buffer.write(this.transactionId);
        buffer.writeUInt32(this.utxoIndex);
        buffer.write(this.assetId);
        buffer.write(inputBuffer);
        return buffer;
    }
    static comparator = (a, b) => {
        return utils_1.JuneoBuffer.comparator(a.serialize(), b.serialize());
    };
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        const transactionId = new types_1.TransactionId(reader.read(constants_1.TransactionIdSize).toCB58());
        const utxoIndex = reader.readUInt32();
        const assetId = new types_1.AssetId(reader.read(constants_1.AssetIdSize).toCB58());
        return new TransferableInput(transactionId, utxoIndex, assetId, this.parseInput(reader.readRemaining()));
    }
    static parseInput(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        const typeId = reader.readUInt32();
        switch (typeId) {
            case constants_1.Secp256k1InputTypeId: {
                return Secp256k1Input.parse(data);
            }
            case constants_1.StakeableLockedInputTypeId: {
                return StakeableLockedInput.parse(data);
            }
            default: {
                throw new utils_1.ParsingError(`unsupported input type id "${typeId}"`);
            }
        }
    }
}
exports.TransferableInput = TransferableInput;
class Secp256k1Input {
    utxo;
    typeId = constants_1.Secp256k1InputTypeId;
    amount;
    addressIndices;
    constructor(amount, addressIndices, utxo) {
        this.utxo = utxo;
        this.amount = amount;
        this.addressIndices = addressIndices;
        this.addressIndices.sort((a, b) => {
            return a - b;
        });
    }
    serialize() {
        const buffer = utils_1.JuneoBuffer.alloc(
        // 4 + 8 + 4 = 16
        16 + 4 * this.addressIndices.length);
        buffer.writeUInt32(this.typeId);
        buffer.writeUInt64(this.amount);
        buffer.writeUInt32(this.addressIndices.length);
        for (const indice of this.addressIndices) {
            buffer.writeUInt32(indice);
        }
        return buffer;
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        reader.readAndVerifyTypeId(constants_1.Secp256k1InputTypeId);
        const amount = reader.readUInt64();
        const addressIndicesCount = reader.readUInt32();
        const indices = [];
        for (let i = 0; i < addressIndicesCount; i++) {
            const indice = reader.readUInt32();
            indices.push(indice);
        }
        return new Secp256k1Input(amount, indices);
    }
}
exports.Secp256k1Input = Secp256k1Input;
class StakeableLockedInput {
    utxo;
    typeId = constants_1.StakeableLockedInputTypeId;
    locktime;
    amount;
    addressIndices;
    constructor(locktime, amount, addressIndices, utxo) {
        this.utxo = utxo;
        this.locktime = locktime;
        this.amount = amount;
        this.addressIndices = addressIndices;
        this.addressIndices.sort((a, b) => {
            return a - b;
        });
    }
    serialize() {
        const buffer = utils_1.JuneoBuffer.alloc(
        // 4 + 8 + 4 + 8 + 4 = 28
        28 + 4 * this.addressIndices.length);
        buffer.writeUInt32(this.typeId);
        buffer.writeUInt64(this.locktime);
        const transferableInput = new Secp256k1Input(this.amount, this.addressIndices, this.utxo);
        buffer.write(transferableInput);
        return buffer;
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        reader.readAndVerifyTypeId(constants_1.StakeableLockedInputTypeId);
        const locktime = reader.readUInt64();
        const input = Secp256k1Input.parse(reader.readRemaining());
        return new StakeableLockedInput(locktime, input.amount, input.addressIndices, input.utxo);
    }
}
exports.StakeableLockedInput = StakeableLockedInput;
